/**
 * `/workflows/portfolio-data/:deal`: a deal's portfolio read as a workflow.
 *
 * The client's workbooks are presentation-shaped — houses across the columns,
 * one tab per plat, rules in the header text. A normalizer has turned them
 * into long, cited tables, and this page is where a person reads those tables
 * and asks about them. The tabs follow the other workflows:
 *
 * - **Tables**: the portfolio by plat, a plat's houses, or every house. Any
 *   figure opens a panel with the workbook cell it came from.
 * - **Needs you**: what was found in the data that a person should look at.
 * - **Data guide**: what every line item means.
 * - **Mapping**: how their sheets became these tables, and why.
 *
 * The plat, the sheet and the year live in the URL; the open figure is
 * page state, and goes to the Analyst with every question. A column the
 * Analyst adds ("add rent per square foot") is a formula the server works out
 * from the tables; its answer ends with a `portfolio-column` block, and
 * drawing that block re-reads the tables so the column shows.
 */

import { Loader2, Sparkles } from "lucide-react";
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DashboardShell, { SHELL_TAB_PARAM } from "../../components/Dashboards/DashboardShell";
import AddedCellPanel from "../../components/PortfolioData/AddedCellPanel";
import AddedColumnCard from "../../components/PortfolioData/AddedColumnCard";
import CellPanel from "../../components/PortfolioData/CellPanel";
import type { CodeBlockRenderers } from "../../components/Chat/codeBlockRenderers";
import DataGuide from "../../components/PortfolioData/DataGuide";
import HouseTable, { type HouseSelection } from "../../components/PortfolioData/HouseTable";
import PlatGroups from "../../components/PortfolioData/PlatGroups";
import PlatList from "../../components/PortfolioData/PlatList";
import PlatTable from "../../components/PortfolioData/PlatTable";
import PortfolioMapping from "../../components/PortfolioData/PortfolioMapping";
import PortfolioNeedsYou from "../../components/PortfolioData/PortfolioNeedsYou";
import RecordPanel from "../../components/PortfolioData/RecordPanel";
import SheetTabs from "../../components/PortfolioData/SheetTabs";
import {
	CapitalSheet,
	LoansSheet,
	OwnersSheet,
	PropertySheet,
} from "../../components/PortfolioData/DataSheets";
import {
	type RecordSelection,
	type SheetId,
	isSheet,
} from "../../components/PortfolioData/sheets";
import {
	buildPortfolioDataContext,
	portfolioChipLabel,
} from "../../components/PortfolioData/portfolioChatBridge";
import {
	HOUSE_COLUMNS,
	addedColumns,
	addedIdOf,
	formatMoney,
	houseRows,
	platRows,
} from "../../components/PortfolioData/portfolioModel";
import RailFallback from "../../components/Chat/RailFallback";
import { useRailThread } from "../../hooks/useRailThread";
import {
	type Portfolio,
	PortfolioNotFoundError,
	getPortfolio,
	removeColumn,
	updateReview,
} from "../../services/portfolioDataService";
import { RUN_THREAD_PARAM } from "../../utils/chatHref";

const PortfolioAnalystRail = lazy(
	() => import("../../components/PortfolioData/PortfolioAnalystRail"),
);

const TABLES_TAB = "tables";
const TAB_IDS: readonly string[] = [TABLES_TAB, "review", "guide", "mapping"];
const PLAT_PARAM = "plat";
/** Plats opened in place on the portfolio view, and houses to point out. */
const OPEN_PARAM = "open";
const HIGHLIGHT_PARAM = "highlight";
const SHEET_PARAM = "sheet";
const YEAR_PARAM = "year";

const formatBuilt = (iso: string | undefined): string => {
	if (!iso) return "";
	const date = new Date(iso);
	return Number.isNaN(date.getTime())
		? ""
		: date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

const PortfolioDataPage: React.FC = () => {
	const { deal = "" } = useParams();
	const dealName = decodeURIComponent(deal);
	const [searchParams, setSearchParams] = useSearchParams();
	const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [selection, setSelection] = useState<HouseSelection | null>(null);
	const [record, setRecord] = useState<RecordSelection | null>(null);
	const [query, setQuery] = useState("");
	const [savingReview, setSavingReview] = useState<string | null>(null);
	const [reviewError, setReviewError] = useState<string | null>(null);

	const handleReview = useCallback(
		async (itemId: string, status: "open" | "known" | "answered", answer?: string) => {
			setSavingReview(itemId);
			setReviewError(null);
			try {
				const state = await updateReview(dealName, itemId, status, answer ?? null);
				setPortfolio((current) => (current ? { ...current, review_state: state } : current));
			} catch (caught) {
				setReviewError(caught instanceof Error ? caught.message : "Could not save that.");
			} finally {
				setSavingReview(null);
			}
		},
		[dealName],
	);

	// Columns the Analyst added are re-read once per column, when its answer
	// has finished arriving.
	const arrivedColumns = useRef(new Set<string>());
	const columnArrived = useCallback(
		(columnId: string) => {
			if (arrivedColumns.current.has(columnId)) return;
			arrivedColumns.current.add(columnId);
			getPortfolio(dealName)
				.then((data) => setPortfolio(data))
				.catch(() => arrivedColumns.current.delete(columnId));
		},
		[dealName],
	);
	const handleRemoveColumn = useCallback(
		async (columnId: string) => {
			const columns = await removeColumn(dealName, columnId);
			setSelection(null);
			setPortfolio((current) => (current ? { ...current, columns } : current));
		},
		[dealName],
	);

	useEffect(() => {
		let live = true;
		setLoading(true);
		getPortfolio(dealName)
			.then((data) => {
				if (!live) return;
				setPortfolio(data);
				setError(null);
			})
			.catch((caught) => {
				if (!live) return;
				setError(
					caught instanceof PortfolioNotFoundError
						? "There are no portfolio tables for this deal, or it isn't one of yours."
						: caught instanceof Error
							? caught.message
							: "Could not load the portfolio data.",
				);
			})
			.finally(() => live && setLoading(false));
		return () => {
			live = false;
		};
	}, [dealName]);

	const requestedTab = searchParams.get(SHELL_TAB_PARAM) ?? TABLES_TAB;
	const activeTab = TAB_IDS.includes(requestedTab) ? requestedTab : TABLES_TAB;
	const platId = searchParams.get(PLAT_PARAM);
	const openParam = searchParams.get(OPEN_PARAM) ?? "";
	const highlightParam = searchParams.get(HIGHLIGHT_PARAM) ?? "";
	const highlighted = useMemo(
		() => new Set(highlightParam.split(",").filter(Boolean)),
		[highlightParam],
	);
	// Opened in place: seeded from the URL (a finding's "See the houses"),
	// then the user's own clicks.
	const [expanded, setExpanded] = useState<Set<string>>(
		() => new Set(openParam.split(",").filter(Boolean)),
	);
	useEffect(() => {
		if (openParam) setExpanded(new Set(openParam.split(",").filter(Boolean)));
	}, [openParam]);

	const sheetParam = searchParams.get(SHEET_PARAM);
	const sheet: SheetId = isSheet(sheetParam) ? sheetParam : "pl";

	// One year on screen at a time, picked in the header; the newest by default.
	const years = portfolio ? [...portfolio.manifest.years].sort((a, b) => b - a) : [];
	const yearParam = Number(searchParams.get(YEAR_PARAM));
	const year = years.includes(yearParam) ? yearParam : (years[0] ?? 0);
	const plat = portfolio?.manifest.plats.find((p) => p.plat_id === platId) ?? null;

	const setParams = useCallback(
		(change: (next: URLSearchParams) => void) => {
			const next = new URLSearchParams(searchParams);
			change(next);
			setSearchParams(next);
		},
		[searchParams, setSearchParams],
	);

	const openPlat = useCallback(
		(id: string | null) => {
			setSelection(null);
			setRecord(null);
			setParams((next) => {
				next.set(SHELL_TAB_PARAM, TABLES_TAB);
				next.delete(OPEN_PARAM);
				next.delete(HIGHLIGHT_PARAM);
				if (id) next.set(PLAT_PARAM, id);
				else next.delete(PLAT_PARAM);
			});
		},
		[setParams],
	);

	const setSheet = (id: SheetId) => {
		setSelection(null);
		setRecord(null);
		setParams((next) => {
			if (id === "pl") next.delete(SHEET_PARAM);
			else next.set(SHEET_PARAM, id);
		});
	};

	const needle = query.trim().toLowerCase();
	const rows = useMemo(() => {
		if (!portfolio) return [];
		const all = houseRows(portfolio, year, plat?.plat_id ?? null);
		if (!needle) return all;
		return all.filter(
			(row) =>
				row.house.house_id.toLowerCase().includes(needle) ||
				String(row.house.address ?? "").toLowerCase().includes(needle) ||
				String(row.house.plat_name ?? "").toLowerCase().includes(needle),
		);
	}, [portfolio, year, plat, needle]);

	// Every house, for the plats opened in place on the portfolio view.
	const allRows = useMemo(() => {
		if (!portfolio) return [];
		return houseRows(portfolio, year, null).filter(
			(row) =>
				!needle ||
				row.house.house_id.toLowerCase().includes(needle) ||
				String(row.house.address ?? "").toLowerCase().includes(needle),
		);
	}, [portfolio, year, needle]);

	// The houses the data sheets list: the plat open, and the search.
	const sheetHouses = useMemo(() => {
		if (!portfolio) return [];
		return portfolio.tables.house.filter(
			(house) =>
				(!plat || house.plat_id === plat.plat_id) &&
				(!needle ||
					house.house_id.toLowerCase().includes(needle) ||
					String(house.address ?? "").toLowerCase().includes(needle)),
		);
	}, [portfolio, plat, needle]);

	const selectedFact = useMemo(() => {
		if (!portfolio || !selection) return null;
		return (
			portfolio.tables.fact.find(
				(f) =>
					f.year === year &&
					f.house_id === selection.houseId &&
					f.line_item === selection.lineItem,
			) ?? null
		);
	}, [portfolio, selection, year]);
	const added = useMemo(() => (portfolio ? addedColumns(portfolio) : []), [portfolio]);
	const selectedAdded = selection
		? (added.find((c) => c.id === addedIdOf(selection.lineItem)) ?? null)
		: null;
	const selectedLabel = selection
		? (selectedAdded?.label ??
			HOUSE_COLUMNS.find((c) => c.key === selection.lineItem)?.label ??
			(selection.lineItem === "vacancy_loss" ? "Vacancy loss" : selection.lineItem))
		: null;

	// ---- Chat ----------------------------------------------------------------
	const {
		threadId: threadParam,
		open: chatOpen,
		setOpen: setChatOpen,
		nameThread,
		close: closeChat,
	} = useRailThread(RUN_THREAD_PARAM);
	const buildChatContext = useCallback(
		() =>
			buildPortfolioDataContext({
				dealName,
				tab: activeTab,
				view: sheet === "pl" ? (plat ? "houses" : "plats") : sheet,
				platId: plat?.plat_id ?? null,
				houseId:
					activeTab === TABLES_TAB
						? sheet === "pl"
							? (selection?.houseId ?? null)
							: (record?.houseId ?? null)
						: null,
				// An added column is not a line item the tables have: the house is
				// what "this" means then.
				lineItem:
					activeTab === TABLES_TAB && sheet === "pl" && selection && !addedIdOf(selection.lineItem)
						? selection.lineItem
						: null,
			}),
		[dealName, activeTab, sheet, plat, selection, record],
	);

	const showColumn = useCallback(
		(columnId: string) => {
			setParams((next) => {
				next.set(SHELL_TAB_PARAM, TABLES_TAB);
				next.delete(SHEET_PARAM);
			});
			window.setTimeout(() => {
				document
					.querySelector(`[data-added-column="${CSS.escape(columnId)}"]`)
					?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
			}, 50);
		},
		[setParams],
	);
	const codeRenderers = useMemo<CodeBlockRenderers>(
		() => ({
			"portfolio-column": (code, context) => {
				const columnId = code.trim();
				return (
					<AddedColumnCard
						columnId={columnId}
						column={portfolio?.columns?.find((c) => c.id === columnId && !c.error) ?? null}
						streaming={context.streaming}
						onArrived={columnArrived}
						onShow={showColumn}
					/>
				);
			},
		}),
		[portfolio, columnArrived, showColumn],
	);

	// Open items only: an answered or known one is done.
	const reviewCount =
		portfolio?.manifest.review.filter((item) => !portfolio.review_state?.[item.id]).length ?? 0;
	const tabs = [
		{ id: TABLES_TAB, label: "Tables" },
		{
			id: "review",
			label: "Needs you",
			...(reviewCount > 0 ? { count: reviewCount, countTone: "attention" as const } : {}),
		},
		{ id: "guide", label: "Data guide" },
		{ id: "mapping", label: "Mapping" },
	];

	const meta = portfolio
		? [
				`${portfolio.manifest.house_count} houses in ${portfolio.manifest.plats.length} plats`,
				`Built ${formatBuilt(portfolio.manifest.built_at)} from ${portfolio.manifest.source_files.length} workbooks`,
				`All ${portfolio.manifest.reconciliation.checked} plat totals match your workbook`,
			]
				.filter(Boolean)
				.join(" · ")
		: "";

	// A panel beside the table takes the plat list's width rather than the
	// table's; the plat is still named in the heading.
	const sidePanelOpen =
		chatOpen || (sheet === "pl" ? Boolean(selection) : Boolean(record));

	const selectFigure = (next: HouseSelection) =>
		setSelection((current) =>
			current?.houseId === next.houseId && current.lineItem === next.lineItem ? null : next,
		);

	const pickRecord = (next: RecordSelection) =>
		setRecord((current) => (current?.key === next.key ? null : next));

	const toggleExpanded = (id: string) =>
		setExpanded((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	// One house-level sheet over the houses given: a plat's, or the search's.
	const renderSheet = (data: Portfolio, houses: typeof sheetHouses) => {
		const props = { portfolio: data, houses, selectedKey: record?.key ?? null, onSelect: pickRecord };
		if (sheet === "property") return <PropertySheet {...props} />;
		if (sheet === "capital") return <CapitalSheet {...props} />;
		if (sheet === "owners") return <OwnersSheet {...props} />;
		return <LoansSheet {...props} year={year} />;
	};

	// The plat's row on a house-level sheet: one line of what the sheet holds.
	const platSummary = (data: Portfolio, id: string): string => {
		const houses = data.tables.house.filter((house) => house.plat_id === id);
		const ids = new Set(houses.map((house) => house.house_id));
		const range = (values: number[], format: (n: number) => string) => {
			if (!values.length) return "";
			const lo = Math.min(...values);
			const hi = Math.max(...values);
			return lo === hi ? format(lo) : `${format(lo)}–${format(hi)}`;
		};
		if (sheet === "property") {
			const built = range(houses.map((h) => Number(h.year_built)).filter(Number.isFinite), String);
			const size = range(
				houses.map((h) => Number(h.total_sqft)).filter(Number.isFinite),
				(n) => n.toLocaleString("en-US"),
			);
			return [built && `built ${built}`, size && `${size} sq ft`].filter(Boolean).join(" · ");
		}
		if (sheet === "capital") {
			const due = data.tables.capital_component.filter(
				(item) => ids.has(item.house_id) && (item.years_past_life ?? 0) > 0,
			);
			const low = due.reduce((sum, item) => sum + (item.replacement_cost_low ?? 0), 0);
			const high = due.reduce((sum, item) => sum + (item.replacement_cost_high ?? 0), 0);
			return due.length
				? `${due.length} items past their life · $${formatMoney(low)}${high !== low ? `–${formatMoney(high)}` : ""} to replace`
				: "nothing past its life";
		}
		if (sheet === "owners") {
			const entities = [
				...new Set(
					data.tables.owner
						.filter((owner) => ids.has(String(owner.house_id)))
						.map((owner) => String(owner.entity_name)),
				),
			];
			return entities.join(", ");
		}
		const loan = data.tables.loan.find((entry) => entry.plat_id === id);
		return loan ? `${loan.bank} loan · $${formatMoney(loan.loan_balance)} balance` : "";
	};

	const renderTables = (data: Portfolio) => {
		const plats = platRows(data, year);
		// What each sheet is, behind the strip's ⓘ rather than as a heading:
		// the plat list already says which plat and how many houses.
		const about = {
			pl:
				!plat
					? "One row per plat. Rent, collections and net income add up the plat's houses; NOI, cap rate and DCR are the plat statement's own. Open a plat with ▸ to see its houses here, or click its row to open it."
					: "One row per house. Every figure is read from your finance workbook unless its column says split (one plat amount divided evenly) or worked out (calculated here). Click a figure to see its cell.",
			property: "Everything your property export holds for each house, under the sheet names in your brief. Columns blank for every house are hidden. Click a cell to see where it is in the export.",
			capital: "Every appliance and big-ticket item and its age in years, shaded once it is past the life its column header gives. Click a cell for its install year and replacement cost.",
			owners: "Ownership rows from your Owner Master.",
			loans: "Each plat's loan, then every house's share of it. The workbook divides one plat loan evenly, so the shares are the same within a plat. Click a share to see its cell.",
		}[sheet];
		return (
			<div className="flex flex-col gap-3.5">
				<div className="flex flex-col gap-4 lg:flex-row lg:items-start">
					{!sidePanelOpen && (
					<PlatList
						plats={data.manifest.plats.map((p) => ({
							platId: p.plat_id,
							platName: p.plat_name,
							houses: p.house_count,
						}))}
						totalHouses={data.manifest.house_count}
						selected={plat?.plat_id ?? null}
						onSelect={openPlat}
						query={query}
						onQueryChange={setQuery}
					/>
					)}
					<section className="flex min-w-0 flex-1 flex-col gap-3">
						<SheetTabs active={sheet} onSelect={setSheet} about={about} />
							{sheet === "pl" && (
								<p className="flex min-w-0 items-center gap-1.5 text-xs text-[#5B6472]">
									Grey figures are split evenly across the plat.
									<span aria-hidden className="mx-1 inline-block h-[7px] w-[7px] rounded-full bg-[#dc6803] align-middle" />
									collected 10% or more under billed.
								</p>
							)}
							{sheet === "pl" && !plat && (
								<PlatTable
									rows={needle ? plats.filter((p) => allRows.some((r) => r.house.plat_id === p.platId)) : plats}
									onOpen={(id) => openPlat(id)}
									expanded={needle ? new Set(plats.map((p) => p.platId)) : expanded}
									onToggle={toggleExpanded}
									renderHouses={(id) => (
										<HouseTable
											rows={allRows.filter((row) => row.house.plat_id === id)}
											grouped={false}
											compact
											highlighted={highlighted}
											selected={selection}
											onSelect={selectFigure}
											added={added}
											year={year}
										/>
									)}
									added={added}
									year={year}
									housesOf={(id) =>
										data.tables.house.filter((h) => h.plat_id === id).map((h) => h.house_id)
									}
								/>
							)}
							{sheet === "pl" && plat && (
								<>
									{rows.length > 0 ? (
										<HouseTable rows={rows} grouped={false} highlighted={highlighted} selected={selection} onSelect={selectFigure} added={added} year={year} />
									) : (
										<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
											No house matches “{query}”.
										</p>
									)}
								</>
							)}
							{sheet === "loans" && !plat && (
								<LoansSheet
									portfolio={data}
									houses={sheetHouses}
									selectedKey={record?.key ?? null}
									onSelect={pickRecord}
									year={year}
									expanded={needle ? new Set(data.manifest.plats.map((p) => p.plat_id)) : expanded}
									onToggle={toggleExpanded}
									onOpenPlat={(id) => openPlat(id)}
								/>
							)}
							{sheet !== "pl" &&
								!(sheet === "loans" && !plat) &&
								(plat ? (
									renderSheet(data, sheetHouses)
								) : (
									<PlatGroups
										groups={data.manifest.plats
											.filter((p) => sheetHouses.some((house) => house.plat_id === p.plat_id))
											.map((p) => ({
												platId: p.plat_id,
												platName: p.plat_name,
												houses: p.house_count,
												summary: platSummary(data, p.plat_id),
											}))}
										expanded={needle ? new Set(data.manifest.plats.map((p) => p.plat_id)) : expanded}
										onToggle={toggleExpanded}
										onOpen={(id) => openPlat(id)}
										render={(id) =>
											renderSheet(
												data,
												sheetHouses.filter((house) => house.plat_id === id),
											)
										}
									/>
								))}
					</section>
				</div>
			</div>
		);
	};

	const renderTab = (tab: string) => {
		if (loading && !portfolio) {
			return (
				<p className="flex items-center gap-2 text-sm text-[#5B6472]">
					<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
					Loading…
				</p>
			);
		}
		if (!portfolio) {
			return (
				<p role="alert" className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
					{error ?? "Could not load the portfolio data."}
				</p>
			);
		}
		switch (tab) {
			case "review":
				return (
					<div className="flex flex-col gap-3">
						{reviewError && (
							<p role="alert" className="text-sm text-[#b42318]">
								{reviewError}
							</p>
						)}
						<PortfolioNeedsYou
							items={portfolio.manifest.review}
							state={portfolio.review_state ?? {}}
							saving={savingReview}
							onOpenPlat={(id) => openPlat(id)}
							onOpenHouses={(houseIds) =>
								setParams((next) => {
									const plats = new Set(
										portfolio.tables.house
											.filter((house) => houseIds.includes(house.house_id))
											.map((house) => house.plat_id),
									);
									next.set(SHELL_TAB_PARAM, TABLES_TAB);
									next.delete(PLAT_PARAM);
									next.delete(SHEET_PARAM);
									next.set(OPEN_PARAM, [...plats].join(","));
									next.set(HIGHLIGHT_PARAM, houseIds.join(","));
								})
							}
							onUpdate={(id, status, answer) => void handleReview(id, status, answer)}
							uploadHref={portfolio.deal_room_id ? `/files/deals/${portfolio.deal_room_id}` : null}
						/>
					</div>
				);
			case "guide":
				return <DataGuide items={portfolio.tables.line_item} />;
			case "mapping":
				return (
					<PortfolioMapping portfolio={portfolio} />
				);
			default:
				return renderTables(portfolio);
		}
	};

	const onTablesTab = activeTab === TABLES_TAB;

	return (
		<DashboardShell
			title={portfolio ? `${portfolio.deal_name} portfolio` : "Portfolio data"}
			meta={meta}
			tabs={tabs}
			actions={
				<>
				{portfolio && (
					<label className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white pl-3 pr-1 text-sm text-[#5B6472]">
						Year
						<select
							value={year}
							onChange={(event) => {
								setSelection(null);
								setParams((next) => next.set(YEAR_PARAM, event.target.value));
							}}
							className="h-7 rounded-md border-0 bg-transparent py-0 pl-1 pr-7 text-sm font-medium leading-7 text-[#111111] focus:outline-none focus:ring-2 focus:ring-[#008782]"
						>
							{years.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
							{years.length === 1 && (
								<option disabled value="">
									{years[0] - 1} · not sent yet
								</option>
							)}
						</select>
					</label>
				)}
				{/* The memo header's Chat button, so every workflow opens its
				    Analyst the same way. */}
				<button
					type="button"
					onClick={() => (chatOpen ? closeChat() : setChatOpen(true))}
					aria-pressed={chatOpen}
					className={`inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-text-strong hover:bg-gray-50 ${
						chatOpen ? "border-[#99CFCD] bg-[#E6F3F3] text-[#004C4B] hover:bg-[#E6F3F3]" : ""
					}`}
				>
					<Sparkles className="h-4 w-4" aria-hidden />
					Chat
				</button>
				</>
			}
			rightRail={
				// One panel at a time, as on the loan tape: the open figure stays
				// marked in the table and named on the chip while Chat is open.
				chatOpen ? (
					<Suspense fallback={<RailFallback />}>
						<PortfolioAnalystRail
							contextLabel={portfolioChipLabel({
								platName: plat?.plat_name ?? null,
								houseId: onTablesTab
									? sheet === "pl"
										? (selection?.houseId ?? null)
										: (record?.houseId ?? null)
									: null,
								label: onTablesTab ? (sheet === "pl" ? selectedLabel : (record?.title ?? null)) : null,
							})}
							buildContext={buildChatContext}
							prefill={null}
							onClose={closeChat}
							initialChatId={threadParam}
							onThreadChange={nameThread}
							codeRenderers={codeRenderers}
						/>
					</Suspense>
				) : onTablesTab && portfolio && sheet !== "pl" && record ? (
					<RecordPanel
						portfolio={portfolio}
						record={record}
						onClose={() => setRecord(null)}
						onAsk={() => setChatOpen(true)}
					/>
				) : onTablesTab && portfolio && sheet === "pl" && selection && selectedAdded ? (
					<AddedCellPanel
						portfolio={portfolio}
						column={selectedAdded}
						houseId={selection.houseId}
						year={year}
						onClose={() => setSelection(null)}
						onAsk={() => setChatOpen(true)}
						onRemove={handleRemoveColumn}
					/>
				) : onTablesTab && portfolio && sheet === "pl" && selectedFact && selectedLabel ? (
					<CellPanel
						portfolio={portfolio}
						fact={selectedFact}
						label={selectedLabel}
						onClose={() => setSelection(null)}
						onAsk={() => setChatOpen(true)}
					/>
				) : undefined
			}
		>
			{(tab) => <div className="space-y-4">{renderTab(tab)}</div>}
		</DashboardShell>
	);
};

export default PortfolioDataPage;
